import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Folder as FolderIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Bookmark as BookmarkIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import ProjectTile from '../components/ProjectTile';
import EstimateTile from '../components/EstimateTile';
import Navigation from '../components/Navigation';
import RoleSwitcherBar from '../components/RoleSwitcherBar';
import SearchBar from '../components/SearchBar';
import FilterPanel from '../components/FilterPanel';
import useDashboardData from '../hooks/useDashboardData';

const ContractorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const {
    projects, estimates, notifications, unreadCount,
    loading, error, setError,
    searchQuery, setSearchQuery,
    estimateSearchQuery, setEstimateSearchQuery,
    projectFilters, setProjectFilters,
    estimateFilters, setEstimateFilters,
    fetchProjects, fetchEstimates,
    notificationsAnchor,
    handleNotificationsClick,
    handleNotificationsClose,
    handleMarkAsRead,
  } = useDashboardData();

  // Contractor-specific state
  const [homeowners, setHomeowners] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [openEstimateDialog, setOpenEstimateDialog] = useState(false);
  const [newEstimate, setNewEstimate] = useState({ name: '', homeowner_id: '' });
  const [inviteHomeowner, setInviteHomeowner] = useState({ email: '', first_name: '', last_name: '' });
  const [invitationSuccess, setInvitationSuccess] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [rooms, setRooms] = useState([
    { name: '', type: 'room' },
    { name: '', type: 'room' },
    { name: '', type: 'room' },
    { name: '', type: 'room' },
    { name: '', type: 'room' },
  ]);

  useEffect(() => {
    fetchHomeowners();
    fetchPaymentSummary();
    fetchTemplates();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/projects/templates');
      setTemplates(response.data.templates || []);
    } catch (err) {
      // non-critical
    }
  };

  const fetchHomeowners = async () => {
    try {
      const response = await axios.get('/api/projects/homeowners');
      setHomeowners(response.data.homeowners);
    } catch (err) {
      // non-critical
    }
  };

  const fetchPaymentSummary = async () => {
    try {
      const response = await axios.get('/api/projects/payment-summary');
      setPaymentSummary(response.data.summary);
    } catch (err) {
      // non-critical
    }
  };

  const handleInviteHomeowner = async () => {
    try {
      setError('');
      setInvitationSuccess('');
      const response = await axios.post('/api/auth/invite-homeowner', {
        email: inviteHomeowner.email,
        first_name: inviteHomeowner.first_name,
        last_name: inviteHomeowner.last_name,
      });
      const { homeowner, already_exists } = response.data;
      await fetchHomeowners();
      setNewEstimate({ ...newEstimate, homeowner_id: homeowner.id });
      setInviteHomeowner({ email: '', first_name: '', last_name: '' });
      setInvitationSuccess(
        already_exists
          ? 'Homeowner already exists and has been selected. They\'ll be notified when you create the estimate.'
          : 'Homeowner invited! We emailed them a link to set up their account.'
      );
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to invite homeowner');
    }
  };

  const handleCreateEstimate = async () => {
    try {
      if (!newEstimate.homeowner_id) {
        setError('Please select a homeowner for the estimate');
        return;
      }

      // Template path: clone the template, assign homeowner, navigate.
      if (selectedTemplateId) {
        const tResponse = await axios.post(`/api/projects/from-template/${selectedTemplateId}`, {
          name: newEstimate.name,
          homeowner_id: newEstimate.homeowner_id,
        });
        const newId = tResponse.data?.estimate?.id;
        resetEstimateDialog();
        if (newId) navigate(`/estimate/${newId}`);
        return;
      }

      const response = await axios.post('/api/estimates', {
        name: newEstimate.name,
        homeowner_id: newEstimate.homeowner_id,
      });
      const createdEstimate = response.data.estimate;
      const roomsWithNames = rooms.filter(room => room.name.trim());
      for (const room of roomsWithNames) {
        await axios.post('/api/rooms', {
          project_id: createdEstimate.id,
          name: room.name,
          type: room.type,
          description: '',
        });
      }
      resetEstimateDialog();
      navigate(`/estimate/${createdEstimate.id}`, { state: { justCreated: true } });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create estimate');
    }
  };

  const resetEstimateDialog = () => {
    setOpenEstimateDialog(false);
    setNewEstimate({ name: '', homeowner_id: '' });
    setInviteHomeowner({ email: '', first_name: '', last_name: '' });
    setInvitationSuccess('');
    setSelectedTemplateId('');
    setRooms([
      { name: '', type: 'room' },
      { name: '', type: 'room' },
      { name: '', type: 'room' },
      { name: '', type: 'room' },
      { name: '', type: 'room' },
    ]);
  };

  const addRoom = () => setRooms([...rooms, { name: '', type: 'room' }]);
  const removeRoom = (index) => setRooms(rooms.filter((_, i) => i !== index));
  const updateRoom = (index, field, value) => {
    const updated = [...rooms];
    updated[index][field] = value;
    setRooms(updated);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Navigation
        onNotificationsClick={handleNotificationsClick}
        unreadCount={unreadCount}
        showNotifications={true}
        showLogout={true}
      />
      <RoleSwitcherBar />

      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: 4, px: { xs: 2, sm: 3 } }}>
        <Box mb={4}>
          <Typography variant="h4" gutterBottom>
            Welcome, {user?.first_name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create estimates for homeowner review. Approved estimates become active projects.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* KPI Row */}
        {(() => {
          const activeCount = projects.filter((p) => p.status === 'active').length;
          const pendingEstimateCount = estimates.filter((e) => e.status === 'pending_approval').length;
          const hasFinancials = paymentSummary && paymentSummary.project_count > 0 && paymentSummary.total_items > 0;
          const fmtCurrency = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

          const cards = [
            {
              label: 'Active Projects',
              value: activeCount,
              caption: activeCount === 1 ? '1 underway' : `${activeCount} underway`,
              color: '#1976d2',
              icon: <FolderIcon sx={{ color: '#1976d2' }} />,
            },
            {
              label: 'Estimates Pending',
              value: pendingEstimateCount,
              caption: pendingEstimateCount === 0 ? 'All clear' : 'Awaiting homeowner',
              color: '#fb8c00',
              icon: <HourglassEmptyIcon sx={{ color: '#fb8c00' }} />,
            },
          ];
          if (hasFinancials) {
            cards.push(
              {
                label: 'Total Budget',
                value: fmtCurrency(paymentSummary.total_budget),
                caption: `${paymentSummary.project_count} active project${paymentSummary.project_count !== 1 ? 's' : ''}`,
                color: '#0d47a1',
                icon: <AccountBalanceIcon sx={{ color: '#0d47a1' }} />,
              },
              {
                label: 'Total Disbursed',
                value: fmtCurrency(paymentSummary.total_disbursed),
                caption: `${paymentSummary.completed_items} of ${paymentSummary.total_items} items`,
                color: '#43a047',
                icon: <MoneyIcon sx={{ color: '#43a047' }} />,
              },
              {
                label: 'Remaining',
                value: fmtCurrency(paymentSummary.remaining),
                caption: paymentSummary.total_budget > 0
                  ? `${Math.round((paymentSummary.total_disbursed / paymentSummary.total_budget) * 100)}% paid`
                  : '0% paid',
                color: '#7c3aed',
                icon: <TrendingUpIcon sx={{ color: '#7c3aed' }} />,
              }
            );
          }
          const colSize = hasFinancials ? { xs: 6, sm: 6, md: 12 / 5 } : { xs: 6, sm: 6, md: 6 };
          return (
            <Grid container spacing={2} mb={3}>
              {cards.map((card) => (
                <Grid item key={card.label} {...colSize}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderTop: `4px solid ${card.color}`,
                      height: '100%',
                      transition: 'transform 0.2s ease',
                      '&:hover': { transform: 'translateY(-2px)' },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      {card.icon}
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {card.label}
                      </Typography>
                    </Box>
                    <Typography variant="h5" fontWeight={700} sx={{ color: card.color, lineHeight: 1.15 }}>
                      {card.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {card.caption}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          );
        })()}

        {/* Primary Action Row */}
        <Paper
          elevation={1}
          sx={{
            p: 2,
            mb: 4,
            borderRadius: 2,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'center',
            justifyContent: { xs: 'stretch', sm: 'flex-start' },
            backgroundColor: 'rgba(13, 71, 161, 0.04)',
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenEstimateDialog(true)}
            sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
          >
            New Estimate
          </Button>
          <Button
            variant="outlined"
            startIcon={<BookmarkIcon />}
            onClick={() => navigate('/templates')}
            sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
          >
            Templates
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: 'auto' } }}>
            Tip: save an estimate as a template to spin up similar jobs in one click.
          </Typography>
        </Paper>

        {/* Estimates Section */}
        {estimates.length > 0 && (
          <Box mb={6}>
            <Typography variant="h5" gutterBottom mb={3}>
              Estimates
            </Typography>
            <Box mb={3}>
              <SearchBar
                placeholder="Search estimates..."
                onSearch={setEstimateSearchQuery}
                defaultValue={estimateSearchQuery}
              />
            </Box>
            <Box mb={3}>
              <FilterPanel
                filters={estimateFilters}
                onFilterChange={setEstimateFilters}
              />
            </Box>
            <Grid container spacing={3}>
              {estimates.map((estimate) => (
                <Grid item xs={12} md={6} key={estimate.id}>
                  <EstimateTile estimate={estimate} onUpdate={fetchEstimates} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Projects Section */}
        <Typography variant="h5" gutterBottom mb={3}>
          Active Projects
        </Typography>
        <Box mb={3}>
          <SearchBar
            placeholder="Search projects..."
            onSearch={setSearchQuery}
            defaultValue={searchQuery}
          />
        </Box>
        <Box mb={3}>
          <FilterPanel
            filters={projectFilters}
            onFilterChange={setProjectFilters}
          />
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : projects.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No active projects yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create an estimate for a homeowner to get started
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {projects.map((project) => (
              <Grid item xs={12} md={6} key={project.id}>
                <ProjectTile project={project} onUpdate={fetchProjects} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Create Estimate Dialog */}
      <Dialog open={openEstimateDialog} onClose={resetEstimateDialog} maxWidth="md" fullWidth>
        <DialogTitle>Create New Estimate</DialogTitle>
        <DialogContent>
          {templates.length > 0 && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(13,71,161,0.04)', borderRadius: 1, border: '1px solid rgba(13,71,161,0.2)' }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <ContentCopyIcon color="primary" fontSize="small" />
                <Typography variant="h6">Start from a template (optional)</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Picking a template clones its rooms, items, and costs into the new estimate.
              </Typography>
              <FormControl fullWidth variant="outlined" sx={{ mt: 1 }}>
                <InputLabel>Template</InputLabel>
                <Select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  label="Template"
                >
                  <MenuItem value=""><em>Start from scratch</em></MenuItem>
                  {templates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name} — {t.room_count} room{t.room_count === '1' ? '' : 's'}, {t.item_count} item{t.item_count === '1' ? '' : 's'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f7fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
            <Typography variant="h6" gutterBottom>Invite New Homeowner</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Don't see the homeowner you're looking for? Invite them by email.
            </Typography>
            {invitationSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInvitationSuccess('')}>
                {invitationSuccess}
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <TextField
                label="First Name"
                variant="outlined"
                size="small"
                value={inviteHomeowner.first_name}
                onChange={(e) => setInviteHomeowner({ ...inviteHomeowner, first_name: e.target.value })}
                sx={{ flex: 1, minWidth: '150px' }}
              />
              <TextField
                label="Last Name"
                variant="outlined"
                size="small"
                value={inviteHomeowner.last_name}
                onChange={(e) => setInviteHomeowner({ ...inviteHomeowner, last_name: e.target.value })}
                sx={{ flex: 1, minWidth: '150px' }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label="Email Address"
                variant="outlined"
                size="small"
                type="email"
                fullWidth
                value={inviteHomeowner.email}
                onChange={(e) => setInviteHomeowner({ ...inviteHomeowner, email: e.target.value })}
              />
              <Button
                variant="contained"
                onClick={handleInviteHomeowner}
                disabled={!inviteHomeowner.email.trim() || !inviteHomeowner.first_name.trim() || !inviteHomeowner.last_name.trim()}
              >
                Invite
              </Button>
            </Box>
          </Box>

          <TextField
            margin="dense"
            label="Estimate Name"
            fullWidth
            variant="outlined"
            value={newEstimate.name}
            onChange={(e) => setNewEstimate({ ...newEstimate, name: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
            <InputLabel>Assign Homeowner (Required)</InputLabel>
            <Select
              value={newEstimate.homeowner_id}
              onChange={(e) => setNewEstimate({ ...newEstimate, homeowner_id: e.target.value })}
              label="Assign Homeowner (Required)"
            >
              <MenuItem value=""><em>Select Homeowner</em></MenuItem>
              {homeowners.map((h) => (
                <MenuItem key={h.id} value={h.id}>
                  {h.first_name} {h.last_name} ({h.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedTemplateId ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Rooms and items will be copied from the selected template. You can edit them after creating.
            </Alert>
          ) : (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Rooms (Add all up front)</Typography>
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addRoom}>
                  Add Room
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add every room for this project now — it's much easier to set them all up at the
                start. You can group rooms into work areas anytime later in the estimate builder.
              </Typography>
              {rooms.map((room, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                  <TextField
                    label="Room Name"
                    fullWidth
                    variant="outlined"
                    value={room.name}
                    onChange={(e) => updateRoom(index, 'name', e.target.value)}
                    placeholder="e.g., Kitchen, Living Room, Master Bedroom"
                  />
                  <IconButton
                    color="error"
                    onClick={() => removeRoom(index)}
                    aria-label={`Remove room ${room.name || index + 1}`}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetEstimateDialog}>Cancel</Button>
          <Button
            onClick={handleCreateEstimate}
            variant="contained"
            disabled={!newEstimate.name.trim() || !newEstimate.homeowner_id}
          >
            Create Estimate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationsAnchor}
        open={Boolean(notificationsAnchor)}
        onClose={handleNotificationsClose}
        PaperProps={{ sx: { width: 320, maxHeight: 400 } }}
      >
        {notifications.length === 0 ? (
          <MenuItem disabled>No notifications</MenuItem>
        ) : (
          notifications.slice(0, 10).map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleMarkAsRead(notification.id)}
              sx={{
                whiteSpace: 'normal',
                backgroundColor: notification.is_read ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
              }}
            >
              <Box>
                <Typography variant="body2">{notification.message}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(notification.created_at).toLocaleString()}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
    </Box>
  );
};

export default ContractorDashboard;
